// src/productions/ProductionsList.js
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE, authHeaders } from '../api';

import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';


import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch { return {}; }
}


function fmtDate(d) {
  if (!d) return '—';
  try {
    const [y, m, day] = String(d).slice(0, 10).split('-');
    if (!y || !m || !day) return String(d).slice(0, 10);
    return `${day}/${m}/${y}`;
  } catch {
    return String(d);
  }
}

function statusLabel(status) {
  switch (status) {
    case 'preparazione': return 'In preparazione';
    case 'riprese':      return 'In riprese';
    case 'post':         return 'In post';
    case 'archived':     return 'Archiviata';
    default:             return 'Da completare';
  }
}

// Colori custom coerenti col tema JKL invece dei colori MUI standard
const STATUS_STYLES = {
  riprese:      { bg: 'rgba(251,191,36,0.15)',  border: 'rgba(251,191,36,0.4)',  color: '#fbbf24', bar: '#fbbf24' },
  post:         { bg: 'rgba(74,222,128,0.12)',  border: 'rgba(74,222,128,0.35)', color: '#4ade80', bar: '#4ade80' },
  preparazione: { bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.35)', color: '#60a5fa', bar: '#60a5fa' },
  archived:     { bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)', color: '#6b7280', bar: '#374151' },
  default:      { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.08)', color: '#6b7280', bar: '#374151' },
};

function getStatusStyle(status) {
  return STATUS_STYLES[status] || STATUS_STYLES.default;
}

// ─── Dialog Nuova/Modifica Produzione ────────────────────────────────────────

function ProductionFormDialog({ open, mode, initial, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [code, setCode] = useState('');
  const [shootingStartDate, setShootingStartDate] = useState('');
  const [postStartDate, setPostStartDate] = useState('');
  const [postStartEstimated, setPostStartEstimated] = useState(false);
  const [airDate, setAirDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showId, setShowId] = useState('');
  const [seasonNumber, setSeasonNumber] = useState('');
  const [shows, setShows] = useState([]);
  const [newShowName, setNewShowName] = useState('');
  const [showMode, setShowMode] = useState('existing'); // existing | new

  useEffect(() => {
    if (!open) return;
    setError('');
    setSaving(false);
    setName(initial?.name || '');
    setClient(initial?.client || '');
    setCode(initial?.code || '');
    setShootingStartDate(initial?.shooting_start_date ? String(initial.shooting_start_date).slice(0, 10) : '');
    setPostStartDate(initial?.post_start_date ? String(initial.post_start_date).slice(0, 10) : '');
    setPostStartEstimated(Boolean(initial?.post_start_is_estimated));
    setAirDate(initial?.air_date ? String(initial.air_date).slice(0, 10) : '');
    setShowId(initial?.show_id ? String(initial.show_id) : '');
    setSeasonNumber(initial?.season_number ? String(initial.season_number) : '');
    // Carica shows dell'azienda
    fetch(`${API_BASE}/productions/shows`, { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } })
      .then(r => r.json()).then(d => setShows(Array.isArray(d) ? d : [])).catch(() => {});
  }, [open, initial]);

  const computedStatus = useMemo(() => {
    if (initial?.archived_at) return 'archived';
    if (!shootingStartDate || !postStartDate) return 'unknown';
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const s = new Date(shootingStartDate);
    const p = new Date(postStartDate);
    if (todayOnly < s) return 'preparazione';
    if (todayOnly >= s && todayOnly < p) return 'riprese';
    return 'post';
  }, [shootingStartDate, postStartDate, initial?.archived_at]);

  const handleSave = async () => {
    setError('');
    if (!showId && !newShowName.trim()) { setError('Seleziona o crea uno show.'); return; }
    if (!shootingStartDate) { setError('Inserisci la data di inizio riprese.'); return; }
    if (!postStartDate) { setError('Inserisci la data di inizio post-produzione.'); return; }
    const s = new Date(shootingStartDate);
    const p = new Date(postStartDate);
    if (!isNaN(s) && !isNaN(p) && p < s) {
      setError('La post-produzione non può iniziare prima delle riprese.');
      return;
    }
    setSaving(true);
    try {
      // Se nuovo show, crealo prima
      let resolvedShowId = showId ? Number(showId) : null;
      if (showMode === 'new' && newShowName.trim()) {
        const sr = await fetch(`${API_BASE}/productions/shows`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
          body: JSON.stringify({ name: newShowName.trim() }),
        });
        const sd = await sr.json();
        if (sr.ok) resolvedShowId = sd.id;
      }
      const payload = {
        name: name.trim(),
        client: client || null,
        code: code || null,
        shooting_start_date: shootingStartDate || null,
        post_start_date: postStartDate || null,
        post_start_is_estimated: Boolean(postStartEstimated),
        air_date: airDate || null,
        show_id: resolvedShowId || null,
        season_number: seasonNumber ? Number(seasonNumber) : null,
      };
      const isEdit = mode === 'edit' && initial?.id;
      const url = isEdit ? `${API_BASE}/productions/${initial.id}` : `${API_BASE}/productions`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Errore salvataggio produzione');
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Errore salvataggio produzione');
    } finally {
      setSaving(false);
    }
  };

  const st = getStatusStyle(computedStatus);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {mode === 'edit' ? 'Modifica produzione' : 'Nuova produzione'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Typography color="error" sx={{ fontWeight: 600, fontSize: 13 }}>
              {error}
            </Typography>
          )}
          {/* Show e stagione */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <Button size="small" variant={showMode==='existing'?'contained':'outlined'} onClick={()=>setShowMode('existing')} sx={{ fontSize: 11 }}>Show esistente</Button>
                <Button size="small" variant={showMode==='new'?'contained':'outlined'} onClick={()=>setShowMode('new')} sx={{ fontSize: 11 }}>Nuovo show</Button>
              </Box>
              {showMode === 'existing' ? (
                <select value={showId} onChange={e=>setShowId(e.target.value)}
                  style={{ width:'100%', background:'#1a1d23', border:'1px solid rgba(255,255,255,0.2)', borderRadius:4, color:'#f0ece4', fontSize:14, padding:'8px 10px', outline:'none', colorScheme:'dark' }}>
                  <option value="">— Nessuno —</option>
                  {shows.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              ) : (
                <TextField label="Nome nuovo show" value={newShowName} onChange={e=>setNewShowName(e.target.value)} fullWidth size="small" placeholder="Es. MasterChef" />
              )}
            </Box>
            <TextField label="N° stagione" type="number" value={seasonNumber} onChange={e=>setSeasonNumber(e.target.value)} sx={{ width: 120, alignSelf: 'flex-end' }} size="small" />
          </Box>
          <TextField label="Cliente / Network" value={client} onChange={(e) => setClient(e.target.value)} fullWidth />
          <TextField label="Codice produzione (opzionale)" value={code} onChange={(e) => setCode(e.target.value)} fullWidth />
          <Divider />
          <Typography variant="subtitle2" sx={{ opacity: 0.7, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Date operative
          </Typography>
          <TextField
            label="Inizio riprese" type="date" value={shootingStartDate}
            onChange={(e) => setShootingStartDate(e.target.value)}
            fullWidth required InputLabelProps={{ shrink: true }}
          />
          <Box>
            <TextField
              label="Inizio post-produzione" type="date" value={postStartDate}
              onChange={(e) => setPostStartDate(e.target.value)}
              fullWidth required InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={<Checkbox checked={postStartEstimated} onChange={(e) => setPostStartEstimated(e.target.checked)} />}
              label={<Typography variant="caption">Data indicativa</Typography>}
            />
          </Box>
          <TextField
            label="Messa in onda (facoltativa)" type="date" value={airDate}
            onChange={(e) => setAirDate(e.target.value)}
            fullWidth InputLabelProps={{ shrink: true }}
          />
          <Divider />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ opacity: 0.6 }}>Stato (automatico):</Typography>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              px: 1.25, py: 0.3, borderRadius: '6px',
              background: st.bg, border: `1px solid ${st.border}`,
              color: st.color, fontSize: 11, fontWeight: 700,
            }}>
              {statusLabel(computedStatus)}
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.45, fontSize: 10 }}>calcolato dalle date</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>Annulla</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Salvataggio…' : mode === 'edit' ? 'Salva modifiche' : 'Crea produzione'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Dialog Elimina ───────────────────────────────────────────────────────────

function DeleteConfirmDialog({ open, production, onClose, onConfirm, deleting }) {
  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Elimina produzione</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>{production?.name || '—'}</Typography>
        <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
          Questa azione è <b>irreversibile</b>. Tutti i piani e gli episodi collegati verranno eliminati.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={deleting} sx={{ color: 'text.secondary' }}>Annulla</Button>
        <Button
          variant="contained" color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={onConfirm} disabled={deleting}
        >
          {deleting ? 'Eliminazione…' : 'Elimina'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Card singola produzione ──────────────────────────────────────────────────

function ProductionCard({ p, onOpen, onEdit, onDelete, onShare, isSupervisor }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const st = getStatusStyle(p.status);

  const openMenu = (e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); };
  const closeMenu = () => setMenuAnchor(null);

  return (
    <>
      <Card
        onClick={() => onOpen(p)}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.07)',
          overflow: 'hidden',
          transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.18s',
          '&:hover': {
            borderColor: 'rgba(212,165,80,0.3)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        {/* Accent bar laterale colorata per status */}
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
          background: st.bar, borderRadius: '12px 0 0 12px',
        }} />

        <CardContent sx={{ pl: 3.5, pr: 2, py: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>

            {/* Contenuto principale */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Titolo + status */}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontSize: 17, fontWeight: 700, lineHeight: 1.2,
                    color: 'text.primary', letterSpacing: 0.1,
                  }}
                >
                  {p.show_name || p.name}
                  {p.season_number ? <span style={{ fontWeight: 400, color: 'rgba(212,165,80,0.7)', fontSize: 14, marginLeft: 8 }}>Stagione {p.season_number}</span> : null}
                </Typography>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center',
                  px: 1.25, py: 0.25, borderRadius: '6px',
                  background: st.bg, border: `1px solid ${st.border}`,
                  color: st.color, fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  flexShrink: 0,
                }}>
                  {statusLabel(p.status)}
                </Box>
              </Stack>

              {/* Cliente + codice */}
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
                {p.client || '—'}
                {p.code && (
                  <Box component="span" sx={{
                    ml: 1, px: 1, py: 0.15, borderRadius: '4px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    fontSize: 11, fontFamily: 'monospace', letterSpacing: '0.04em',
                    color: 'text.secondary',
                  }}>
                    {p.code}
                  </Box>
                )}
              </Typography>

              {/* Date in riga compatta */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, alignItems: { sm: 'center' } }}>
                <DateItem label="Riprese" value={fmtDate(p.shooting_start_date)} />
                <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '1px', height: 28, bgcolor: 'divider', mx: 2.5, flexShrink: 0 }} />
                <DateItem
                  label="Inizio post"
                  value={fmtDate(p.post_start_date)}
                  badge={p.post_start_is_estimated ? 'est.' : null}
                />
                <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '1px', height: 28, bgcolor: 'divider', mx: 2.5, flexShrink: 0 }} />
                <DateItem label="Messa in onda" value={fmtDate(p.air_date)} />
              </Box>
            </Box>

            {/* Azioni */}
            <Box sx={{ flexShrink: 0, pt: 0.25 }}>
              <Tooltip title="Azioni">
                <IconButton
                  size="small"
                  onClick={openMenu}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { minWidth: 160 } }}
      >
        <MenuItem onClick={() => { closeMenu(); onOpen(p); }}>
          <Box component="span" sx={{ mr: 1.5, fontSize: 15 }}>↗</Box> Apri
        </MenuItem>
        <MenuItem onClick={() => { closeMenu(); onEdit(p); }}>
          <EditOutlinedIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.7 }} /> Modifica
        </MenuItem>
        {isSupervisor && (
          <MenuItem onClick={() => { closeMenu(); onShare(p); }}>
            <Box component="span" sx={{ mr: 1.5, fontSize: 15 }}>👥</Box> Condividi
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { closeMenu(); onDelete(p); }} sx={{ color: 'error.main' }}>
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1.5 }} /> Elimina
        </MenuItem>
      </Menu>
    </>
  );
}

function DateItem({ label, value, badge }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10, color: 'text.disabled', letterSpacing: '0.07em', textTransform: 'uppercase', mb: 0.2 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: value === '—' ? 'text.disabled' : 'text.primary' }}>
          {value}
        </Typography>
        {badge && (
          <Box sx={{
            px: 0.75, py: 0.1, borderRadius: '4px',
            background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.25)',
            color: '#60a5fa', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            {badge}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ─── Lista principale ─────────────────────────────────────────────────────────

export default function ProductionsList() {
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formInitial, setFormInitial] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [coordinators, setCoordinators] = useState([]);
  const [shareCoordinatorId, setShareCoordinatorId] = useState('');
  const [sharing, setSharing] = useState(false);
  const token = localStorage.getItem('token') || '';
  const isSupervisor = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.company_role === 'supervisor'; } catch { return false; }
  })();

  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE}/productions`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setProductions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Errore caricamento produzioni', e);
      setProductions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => { setFormMode('create'); setFormInitial(null); setFormOpen(true); };
  const startEdit   = (p) => { setFormMode('edit');   setFormInitial(p);    setFormOpen(true); };
  const startDelete = (p) => { setDeleteTarget(p); setDeleteOpen(true); };
  const startShare = async (p) => {
    setShareTarget(p);
    setShareCoordinatorId('');
    try {
      const data = await fetch(`${API_BASE}/supervisor/coordinators`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      setCoordinators(data.coordinators || []);
    } catch { setCoordinators([]); }
    setShareOpen(true);
  };
  const doShare = async () => {
    if (!shareCoordinatorId || !shareTarget) return;
    setSharing(true);
    try {
      await fetch(`${API_BASE}/productions/${shareTarget.id}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ coordinatorId: Number(shareCoordinatorId) }),
      });
      setShareOpen(false);
      setShareTarget(null);
    } catch (e) { alert(e.message); }
    finally { setSharing(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE}/productions/${deleteTarget.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Errore eliminazione produzione');
      setDeleteOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Errore eliminazione produzione');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Torna alla dashboard">
              <IconButton onClick={() => navigate('/')} size="small" sx={{ color: 'text.secondary' }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Breadcrumbs sx={{ '& .MuiBreadcrumbs-separator': { opacity: 0.4 } }}>
              <Link
                underline="hover" color="text.secondary"
                onClick={() => navigate('/')}
                sx={{ cursor: 'pointer', fontSize: 13 }}
              >
                Dashboard
              </Link>
              <Typography sx={{ fontSize: 13, color: 'text.primary', fontWeight: 600 }}>
                Produzioni
              </Typography>
            </Breadcrumbs>
            {!loading && productions.length > 0 && (
              <Box sx={{
                px: 1.25, py: 0.2, borderRadius: '20px',
                background: 'rgba(212,165,80,0.1)', border: '1px solid rgba(212,165,80,0.25)',
                color: '#d4a550', fontSize: 11, fontWeight: 700,
              }}>
                {productions.length}
              </Box>
            )}
          </Stack>

          <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate}>
            Nuova produzione
          </Button>
        </Stack>

        {/* Contenuto */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} sx={{ color: '#d4a550' }} />
          </Box>
        ) : productions.length === 0 ? (
          <Box sx={{
            textAlign: 'center', py: 10,
            border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '16px',
          }}>
            <Typography sx={{ fontSize: 32, mb: 2 }}>🎬</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Nessuna produzione</Typography>
            <Typography sx={{ opacity: 0.5, mb: 3, fontSize: 14 }}>
              Crea la prima produzione per iniziare a pianificare team e post.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate}>
              Crea produzione
            </Button>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {productions.map((p) => (
              <ProductionCard
                key={p.id}
                p={p}
                onOpen={(prod) => navigate(`/productions/${prod.id}`)}
                onEdit={startEdit}
                onDelete={startDelete}
                onShare={startShare}
                isSupervisor={isSupervisor}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {/* Dialogs */}
      <ProductionFormDialog
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        onClose={() => setFormOpen(false)}
        onSaved={() => load()}
      />
      <DeleteConfirmDialog
        open={deleteOpen}
        production={deleteTarget}
        deleting={deleting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />

      {/* Modal condivisione produzione */}
      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Kanit', sans-serif", fontWeight: 700 }}>
          Condividi produzione
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 2, color: 'text.secondary' }}>
            Seleziona un coordinatore a cui dare accesso a <b>{shareTarget?.name}</b>.
          </Typography>
          {coordinators.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Nessun coordinatore disponibile.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {coordinators.map(c => (
                <Box key={c.id}
                  onClick={() => setShareCoordinatorId(String(c.id))}
                  sx={{
                    p: 1.5, borderRadius: 2, cursor: 'pointer',
                    border: shareCoordinatorId === String(c.id) ? '1px solid rgba(212,165,80,0.5)' : '1px solid rgba(255,255,255,0.07)',
                    background: shareCoordinatorId === String(c.id) ? 'rgba(212,165,80,0.08)' : 'transparent',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                  }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(212,165,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#d4a550' }}>
                    {(c.nome?.[0] || '') + (c.cognome?.[0] || '')}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{c.nome} {c.cognome}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{c.email}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Annulla</Button>
          <Button variant="contained" disabled={!shareCoordinatorId || sharing} onClick={doShare}
            sx={{ bgcolor: '#d4a550', color: '#0e1116', '&:hover': { bgcolor: '#c49440' } }}>
            {sharing ? 'Salvataggio...' : 'Condividi'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}
